<template>
    <v-dialog
            v-model="dialog"
            max-width="850"
            transition="dialog-bottom-transition"
            content-class="premium-stats-modal"
            scrollable
            persistent
    >
        <v-card color="#161616" dark class="d-flex flex-column modal-card-wrapper">

            <v-toolbar color="#161616" dense flat class="modal-header flex-shrink-0">
                <v-icon color="#00e5ff" class="mr-3" small>mdi-poll</v-icon>
                <v-toolbar-title class="subtitle-2 font-weight-bold text-uppercase tracking-wide">
                    MATCH CENTER
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <span class="caption grey--text font-weight-bold mx-4" v-if="isValidData">
                    {{ matchStatusLabel }}
                </span>
                <v-btn icon @click="close" small>
                    <v-icon color="#aaa">mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pa-0 flex-grow-1 overflow-hidden d-flex flex-column" v-if="isValidData">

                <div class="stats-hero pt-8 pb-6 px-4 flex-shrink-0">
                    <div class="d-flex justify-space-between align-center px-8">

                        <div class="team-block text-center" style="width: 35%;">
                            <div class="font-weight-bold text-h6 mb-3 team-name">{{ event.home }}</div>
                            <v-icon x-small color="#ffff00" v-if="isServing('1')" class="serve-indicator mb-1">{{ sportBallIcon }}</v-icon>
                            <div class="score-display">{{ getScore('1') }}</div>
                        </div>

                        <div class="center-block d-flex flex-column align-center justify-center">
                            <span class="vs-text mb-2">VS</span>

                            <div class="d-flex flex-wrap justify-center caption grey--text mb-3 font-weight-bold" v-if="previousPeriodsScores.length > 0">
                                <span v-for="(score, idx) in previousPeriodsScores" :key="idx" class="mx-1">
                                    {{ score }}
                                </span>
                            </div>

                            <div class="status-badge">
                                {{ matchStatusLabel }}
                            </div>

                            <div class="mt-4" v-if="computedExtraInfo.length > 0">
                                <div v-for="(info, i) in computedExtraInfo" :key="i" class="extra-info-pill mx-1 mb-1">
                                    <span class="pill-label">{{ info.name }}:</span>
                                    <span class="pill-value">{{ info.value }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="team-block text-center" style="width: 35%;">
                            <div class="font-weight-bold text-h6 mb-3 team-name">{{ event.away }}</div>
                            <v-icon x-small color="#ffff00" v-if="isServing('2')" class="serve-indicator mb-1">{{ sportBallIcon }}</v-icon>
                            <div class="score-display">{{ getScore('2') }}</div>
                        </div>
                    </div>
                </div>

                <v-tabs v-model="activeTab" background-color="#161616" color="#00e5ff" grow height="50" class="custom-tabs flex-shrink-0">
                    <v-tab class="font-weight-bold tab-text">STATS</v-tab>
                    <v-tab class="font-weight-bold tab-text">TIMELINE</v-tab>
                    <v-tab class="font-weight-bold tab-text">LINEUPS</v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="flex-grow-1 overflow-y-auto content-area">

                    <v-tab-item>
                        <div class="pa-5">
                            <div v-if="computedStats.length > 0">
                                <div v-for="stat in computedStats" :key="stat.type" class="custom-stat-row mb-6">
                                    <div class="d-flex justify-space-between align-center mb-2 px-1">
                                        <span class="stat-number">{{ stat.h }}</span>
                                        <span class="stat-label d-flex align-center justify-center">
                                            <v-icon x-small color="#888" class="mr-2" v-if="stat.icon">{{ stat.icon }}</v-icon>
                                            {{ stat.label }}
                                        </span>
                                        <span class="stat-number">{{ stat.a }}</span>
                                    </div>
                                    <div class="bars-container d-flex justify-space-between">
                                        <div class="bar-bg mr-1 d-flex justify-end">
                                            <div class="bar-fill home-color" :style="{ width: stat.h_perc + '%' }"></div>
                                        </div>
                                        <div class="bar-bg ml-1 d-flex justify-start">
                                            <div class="bar-fill away-color" :style="{ width: stat.a_perc + '%' }"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="empty-state">
                                <v-icon size="40" color="#333">mdi-chart-bar-off</v-icon>
                                <div class="mt-2 caption grey--text">No statistics available</div>
                            </div>
                        </div>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="pa-5 timeline-wrapper">
                            <div v-if="computedIncidents.length > 0">
                                <div v-for="(inc, i) in computedIncidents" :key="i" class="timeline-row mb-4">
                                    <div class="time-col">{{ formatTime(inc) }}</div>

                                    <div class="incident-card" :class="getSideClass(inc.side)">
                                        <div class="d-flex align-center w-100">
                                            <div class="incident-badge mr-3 d-flex align-center justify-center">
                                                <v-icon x-small :color="inc.color || '#00e5ff'">{{ inc.icon }}</v-icon>
                                            </div>
                                            <div class="incident-text flex-grow-1 text-uppercase">
                                                {{ inc.label }}
                                                <span class="text-capitalize ml-2 grey--text caption" v-if="inc.player">- {{ inc.player }}</span>
                                            </div>
                                            <div class="incident-score" v-if="inc.score">{{ inc.score }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="empty-state">
                                <v-icon size="40" color="#333">mdi-clock-outline</v-icon>
                                <div class="mt-2 caption grey--text">No events yet</div>
                            </div>
                        </div>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="pa-5">
                            <div v-if="computedLineups.home.length || computedLineups.away.length" class="d-flex lineups-container">
                                <div class="w-50 pr-4 border-right-dark">
                                    <div class="font-weight-bold text-center mb-4 team-name">{{ event.home }}</div>
                                    <div v-for="(player, idx) in computedLineups.home" :key="'h'+idx" class="d-flex align-center mb-2 lineup-row">
                                        <div class="shirt-number mr-3" v-if="player.number">{{ player.number }}</div>
                                        <v-icon x-small color="#00e5ff" class="mr-2" v-else>mdi-tshirt-crew</v-icon>
                                        <div class="player-name text-truncate">{{ player.name }}</div>
                                    </div>
                                </div>
                                <div class="w-50 pl-4">
                                    <div class="font-weight-bold text-center mb-4 team-name">{{ event.away }}</div>
                                    <div v-for="(player, idx) in computedLineups.away" :key="'a'+idx" class="d-flex align-center mb-2 lineup-row flex-row-reverse">
                                        <div class="shirt-number ml-3" v-if="player.number">{{ player.number }}</div>
                                        <v-icon x-small color="#ff3d00" class="ml-2" v-else>mdi-tshirt-crew</v-icon>
                                        <div class="player-name text-truncate text-right">{{ player.name }}</div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="empty-state">
                                <v-icon size="40" color="#333">mdi-account-group</v-icon>
                                <div class="mt-2 caption grey--text">Lineups not available for this match</div>
                            </div>
                        </div>
                    </v-tab-item>

                </v-tabs-items>
            </v-card-text>

            <v-card-text class="d-flex flex-column justify-center align-center flex-grow-1" v-else>
                <v-progress-circular indeterminate color="#00e5ff" size="40"></v-progress-circular>
                <div class="mt-4 grey--text caption text-uppercase">Loading Match Data...</div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    // --- MAPPATURE ---
    const STATS_MAP = {
        // Calcio
        1: { label: 'Corners', icon: 'mdi-flag-variant' },
        2: { label: 'Shots On Target', icon: 'mdi-target' },
        3: { label: 'Shots Off Target', icon: 'mdi-arrow-top-right' },
        4: { label: 'Attacks', icon: 'mdi-sword' },
        5: { label: 'Dangerous Attacks', icon: 'mdi-alert-octagon' },
        6: { label: 'Yellow Cards', icon: 'mdi-card' },
        7: { label: 'Red Cards', icon: 'mdi-card' },
        8: { label: 'Penalties', icon: 'mdi-exclamation' },
        9: { label: 'Goals', icon: 'mdi-soccer' },
        11: { label: 'Possession %', icon: 'mdi-chart-pie' },
        12: { label: 'Fouls', icon: 'mdi-whistle' },
        13: { label: 'Free Kicks', icon: 'mdi-shoe-cleat' },
        14: { label: 'Goal Kicks', icon: 'mdi-soccer-field' },
        15: { label: 'Offsides', icon: 'mdi-flag-off' },
        17: { label: 'Throw Ins', icon: 'mdi-hand-ball' },
        2046: { label: 'Total Shots', icon: 'mdi-soccer' },

        // Tennis / Tennistavolo
        20: { label: 'Aces', icon: 'mdi-tennis-ball' },
        21: { label: 'Double Faults', icon: 'mdi-alert-circle-outline' },
        22: { label: 'Service Points', icon: 'mdi-tennis' },
        23: { label: 'Break Points', icon: 'mdi-target-variant' },
        34: { label: '1st Serve Wins', icon: 'mdi-trophy-variant-outline' },
        66: { label: 'Break Pts Converted', icon: 'mdi-check' },
        67: { label: 'Break Pts Saved', icon: 'mdi-shield-check' },
        2008: { label: 'Points Won', icon: 'mdi-tennis' },
        2194: { label: '1st Serve %', icon: 'mdi-percent' },
        2195: { label: '1st Serve Pts Won', icon: 'mdi-tennis' },
        2196: { label: '2nd Serve Pts Won', icon: 'mdi-tennis' },

        // Cricket
        93: { label: 'Runs', icon: 'mdi-cricket' },
        154: { label: 'Minutes Batted', icon: 'mdi-clock-outline' },
        155: { label: 'Overs Bowled', icon: 'mdi-cricket' },
        156: { label: 'Boundary Sixes', icon: 'mdi-numeric-6-box-outline' },
        160: { label: 'Extras', icon: 'mdi-plus' },
        161: { label: 'No Balls', icon: 'mdi-cancel' },
        164: { label: 'Balls Bowled', icon: 'mdi-baseball' },
        166: { label: 'Run Rate', icon: 'mdi-chart-line' },
        170: { label: 'Boundary Fours', icon: 'mdi-numeric-4-box-outline' },
        1078: { label: 'Partnership Runs', icon: 'mdi-account-multiple' }
    };

    const INCIDENTS_MAP = {
        1: { label: 'Corner', icon: 'mdi-flag-variant', color: '#fff' },
        6: { label: 'Yellow Card', icon: 'mdi-card', color: '#ffeb3b' },
        7: { label: 'Red Card', icon: 'mdi-card', color: '#f44336' },
        8: { label: 'Penalty', icon: 'mdi-alert-circle', color: '#ff9800' },
        9: { label: 'Goal', icon: 'mdi-soccer', color: '#00e5ff' },
        10: { label: 'Substitution', icon: 'mdi-account-switch', color: '#aaa' },
        24: { label: 'Own Goal', icon: 'mdi-soccer', color: '#ff3d00' },
        25: { label: 'Penalty Goal', icon: 'mdi-soccer', color: '#00e5ff' },
        27: { label: 'Score Update', icon: 'mdi-scoreboard-outline', color: '#00e5ff' },
        1074: { label: 'VAR', icon: 'mdi-monitor', color: '#ff9800' },
        20: { label: 'Ace', icon: 'mdi-tennis-ball', color: '#8bc34a' },
        21: { label: 'Double Fault', icon: 'mdi-alert-circle-outline', color: '#f44336' },
        22: { label: 'Service Point', icon: 'mdi-tennis', color: '#00e5ff' },
        36: { label: 'Wickets Taken', icon: 'mdi-cricket', color: '#f44336' },
        1076: { label: 'Fall of Wicket', icon: 'mdi-cricket', color: '#ff3d00' },
        182: { label: 'Four', icon: 'mdi-numeric-4-box', color: '#00e5ff' },
        183: { label: 'Six', icon: 'mdi-numeric-6-box', color: '#ffeb3b' }
    };

    const PERIODS_MAP = {
        "6046": { 10: "1st Half", 20: "2nd Half", 30: "Overtime 1st Half", 35: "Overtime 2nd Half", 40: "Overtime", 50: "Penalties", 100: "Full time" },
        "54094": { 1: "1st Set", 2: "2nd Set", 3: "3rd Set", 4: "4th Set", 5: "5th Set", 60: "Game", 61: "Tie Break", 100: "Full time" },
        "265917": { 1: "1st Set", 2: "2nd Set", 3: "3rd Set", 4: "4th Set", 5: "5th Set", 6: "6th Set", 7: "7th Set", 100: "Full time" },
        "48242": { 1: "1st Quarter", 2: "2nd Quarter", 3: "3rd Quarter", 4: "4th Quarter", 40: "Overtime", 100: "Full time" },
        "452674": { 10: "1st Inn (Home)", 11: "1st Inn (Away)", 20: "2nd Inn (Home)", 21: "2nd Inn (Away)", 70: "Over", 71: "Delivery", 80: "Super Over", 100: "Full time" }
    };

    export default {
        name: 'LiveScoreModal',
        props: {
            value: Boolean,
            event: Object
        },
        data: () => ({
            activeTab: 0
        }),
        computed: {
            dialog: {
                get() { return this.value; },
                set(val) { this.$emit('input', val); }
            },
            lsData() {
                if (this.event && this.event.livescore && this.event.livescore.scoreboard) {
                    return this.event.livescore;
                }
                if (this.event && this.event.fixtureLiveScore && this.event.fixtureLiveScore.length > 0 && this.event.fixtureLiveScore[0].scoreboard) {
                    return this.event.fixtureLiveScore[0];
                }
                return null;
            },
            isValidData() {
                return this.lsData !== null && this.lsData.scoreboard !== undefined;
            },
            isTennisOrTableTennis() {
                return this.event && (String(this.event.sportId) === "54094" || String(this.event.sportId) === "265917");
            },
            isCricket() { return this.event && String(this.event.sportId) === "452674"; },

            sportBallIcon() {
                if (!this.event) return 'mdi-circle-medium';
                const sId = String(this.event.sportId);
                if (sId === '6046') return 'mdi-soccer';
                if (sId === '54094' || sId === '265917') return 'mdi-tennis-ball';
                if (sId === '48242') return 'mdi-basketball';
                if (sId === '154914') return 'mdi-baseball';
                return 'mdi-circle-medium';
            },

            matchStatusLabel() {
                if (!this.isValidData) return 'Live';
                const periodId = this.lsData.scoreboard.currentPeriod;
                const time = this.lsData.scoreboard.time;

                const sportPeriods = PERIODS_MAP[String(this.event.sportId)];
                let status = (sportPeriods && sportPeriods[periodId]) ? sportPeriods[periodId] : `Period ${periodId}`;

                if (time && time !== '-1' && !this.isTennisOrTableTennis && !this.isCricket) {
                    status += ` • ${Math.floor(parseInt(time)/60)}'`;
                }
                return status;
            },

            previousPeriodsScores() {
                if (!this.isValidData || !this.lsData.periods) return [];
                return this.lsData.periods
                    .filter(p => {
                        const t = parseInt(p.type);
                        const curr = parseInt(this.lsData.scoreboard.currentPeriod);
                        if (this.isTennisOrTableTennis && (curr === 60 || curr === 61)) return t <= 5 && p.isFinished;
                        return t < curr && p.isFinished;
                    })
                    .sort((a,b) => parseInt(a.type) - parseInt(b.type))
                    .map(p => {
                        const r1 = p.results ? p.results.find(r => String(r.position) === "1") : null;
                        const r2 = p.results ? p.results.find(r => String(r.position) === "2") : null;
                        const h = r1 ? r1.value : 0;
                        const a = r2 ? r2.value : 0;
                        return `${h}-${a}`;
                    });
            },

            computedStats() {
                if (!this.isValidData || !this.lsData.statistics) return [];
                return this.lsData.statistics
                    .map(s => {
                        const typeId = parseInt(s.type);
                        const conf = STATS_MAP[typeId];
                        if (!conf) return null;

                        const r1 = s.results ? s.results.find(r => String(r.position) === "1") : null;
                        const r2 = s.results ? s.results.find(r => String(r.position) === "2") : null;

                        const h = parseFloat(r1 ? r1.value : 0);
                        const a = parseFloat(r2 ? r2.value : 0);
                        const total = h + a;

                        return {
                            type: typeId,
                            label: conf.label,
                            icon: conf.icon,
                            h, a,
                            h_perc: total > 0 ? (h / total) * 100 : (h > 0 ? 100 : (a > 0 ? 0 : 50)),
                            a_perc: total > 0 ? (a / total) * 100 : (a > 0 ? 100 : (h > 0 ? 0 : 50))
                        };
                    })
                    .filter(s => s !== null)
                    .sort((a,b) => a.type - b.type);
            },

            computedIncidents() {
                if (!this.isValidData) return [];
                let list = [];

                // 1. Array "periods"
                if (this.lsData.periods) {
                    this.lsData.periods.forEach(period => {
                        if(period.incidents && Array.isArray(period.incidents)) {
                            period.incidents.forEach(inc => {
                                const formatted = this.formatIncident(inc);
                                if(formatted) list.push(formatted);
                            });
                        }
                    });
                }

                // 2. Array "statistics"
                if (this.lsData.statistics) {
                    this.lsData.statistics.forEach(stat => {
                        if(stat.incidents && Array.isArray(stat.incidents)) {
                            stat.incidents.forEach(inc => {
                                const formatted = this.formatIncident(inc);
                                if(formatted) list.push(formatted);
                            });
                        }
                    });
                }

                return list.sort((a,b) => b.seconds - a.seconds);
            },

            computedExtraInfo() {
                if (!this.isValidData || !this.lsData.livescoreExtraData) return [];
                const displayList = [];
                this.lsData.livescoreExtraData.forEach(item => {
                    let label = item.name;
                    if (label === 'CourtSurfaceType') label = 'SURFACE';

                    if (label === 'Turn' || label === 'Possession' || label === 'Serve Number' || label === 'WithInPlay' || label === 'WithLivescore') return;

                    displayList.push({ name: label, value: item.value });
                });
                return displayList;
            },

            computedLineups() {
                if (!this.isValidData || !this.lsData.playerStatistics) return { home: [], away: [] };

                let home = [];
                let away = [];
                const homeId = String(this.event.home_id);
                const awayId = String(this.event.away_id);

                this.lsData.playerStatistics.forEach(p => {
                    let playerObj = {
                        id: p.id || p.playerId || Math.random(),
                        name: p.playerName || p.name || 'Unknown',
                        number: p.shirtNumber || p.jerseyNumber || ''
                    };

                    if (String(p.teamId) === homeId || String(p.participantPosition) === "1") {
                        home.push(playerObj);
                    } else if (String(p.teamId) === awayId || String(p.participantPosition) === "2") {
                        away.push(playerObj);
                    }
                });

                return { home, away };
            }
        },
        methods: {
            close() { this.dialog = false; },

            getScore(pos) {
                if(!this.isValidData || !this.lsData.scoreboard || !this.lsData.scoreboard.results) return '0';
                const res = this.lsData.scoreboard.results.find(r => String(r.position) === String(pos));
                return res ? res.value : '0';
            },

            isServing(pos) {
                if(!this.isValidData || !this.lsData.livescoreExtraData) return false;

                // Per il Tennis / Table Tennis (Turn)
                const srv = this.lsData.livescoreExtraData.find(x => x.name === 'Turn' || x.name === 'Serving');
                if (srv && String(srv.value) === String(pos)) return true;

                // Per il Calcio (Possession)
                const poss = this.lsData.livescoreExtraData.find(x => x.name === 'Possession');
                if (poss) {
                    if (String(pos) === '1' && String(poss.value).toLowerCase() === 'home') return true;
                    if (String(pos) === '2' && String(poss.value).toLowerCase() === 'away') return true;
                }

                return false;
            },

            formatTime(inc) {
                if (this.isTennisOrTableTennis) return 'Set';
                if (inc.seconds && inc.seconds > 0) return Math.floor(inc.seconds / 60) + "'";
                return '•';
            },

            formatIncident(inc) {
                const typeId = parseInt(inc.incidentType);
                const conf = INCIDENTS_MAP[typeId];
                if(!conf) return null;

                let scoreStr = '';
                // Calcola il punteggio SOLO se è effettivamente un Goal(9) o uno Score Update(27)
                if((typeId === 9 || typeId === 27) && inc.results && inc.results.length >= 2) {
                    const r1 = inc.results.find(r => String(r.position) === "1");
                    const r2 = inc.results.find(r => String(r.position) === "2");
                    if(r1 && r2) scoreStr = `${r1.value}-${r2.value}`;
                }

                let playerName = null;
                if (inc.playerName && inc.playerName !== "-1" && inc.playerName !== "Unknown") {
                    playerName = inc.playerName;
                }

                return {
                    ...conf,
                    seconds: parseInt(inc.seconds ? inc.seconds : 0),
                    side: String(inc.participantPosition),
                    player: playerName,
                    score: scoreStr
                };
            },

            getSideClass(side) {
                if (side === '1') return 'border-home';
                if (side === '2') return 'border-away';
                return 'border-neutral';
            }
        }
    }
</script>

<style scoped>
    /* BACKGROUND E STRUTTURA */
    ::v-deep .premium-stats-modal {
        border-radius: 6px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.9) !important;
    }
    .modal-card-wrapper {
        background-color: #161616 !important;
    }
    .modal-header {
        border-bottom: 1px solid #222 !important;
    }
    .tracking-wide { letter-spacing: 1px; }

    /* HERO SCOREBOARD */
    .stats-hero {
        background-color: #1a1a1a;
        border-bottom: 1px solid #2a2a2a;
    }
    .team-name {
        color: #fff;
        font-size: var(--font-size-7xl);
    }
    .score-display {
        font-size: var(--font-size-20xl);
        font-weight: 900;
        line-height: 1;
        color: #fff;
    }
    .vs-text {
        font-size: var(--font-size-10xl);
        font-weight: 300;
        color: #555;
        letter-spacing: 2px;
    }
    .status-badge {
        border: 1px solid #00e5ff;
        color: #00e5ff;
        padding: 2px 14px;
        border-radius: 20px;
        font-size: var(--font-size-2xl);
        font-weight: 700;
        background-color: rgba(0, 229, 255, 0.05);
    }
    .serve-indicator {
        animation: pulse 1.5s infinite;
    }

    /* PILL EXTRA INFO (Es. SURFACE) */
    .extra-info-pill {
        border: 1px solid #333;
        border-radius: 20px;
        padding: 2px 12px;
        background-color: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .pill-label {
        color: #888;
        font-size: var(--font-size-lg);
        text-transform: uppercase;
        margin-right: 4px;
        font-weight: 600;
    }
    .pill-value {
        color: #fff;
        font-size: var(--font-size-2xl);
        font-weight: 400;
    }

    /* TABS STILE MINIMAL */
    .custom-tabs {
        border-bottom: 1px solid #2a2a2a;
    }
    ::v-deep .v-tab {
        color: #666 !important;
        font-size: var(--font-size-2xl);
        letter-spacing: 1px;
    }
    ::v-deep .v-tab--active {
        color: #00e5ff !important;
    }
    .content-area {
        background-color: #161616 !important;
    }

    /* BARRE STATISTICHE SOTTILI */
    .custom-stat-row {
        width: 100%;
    }
    .stat-number {
        font-weight: 900;
        font-size: var(--font-size-4xl);
        color: #fff;
        width: 25px;
    }
    .stat-number:first-child { text-align: left; }
    .stat-number:last-child { text-align: right; }

    .stat-label {
        font-size: var(--font-size-2xl);
        color: #aaa;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        flex-grow: 1;
    }
    .bars-container {
        height: 4px;
    }
    .bar-bg {
        width: 49.5%;
        background-color: #2a2a2a;
        border-radius: 2px;
        overflow: hidden;
    }
    .bar-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.5s ease-out;
    }
    .home-color { background-color: #00e5ff; }
    .away-color { background-color: #ff3d00; }

    /* TIMELINE */
    .timeline-wrapper {
        position: relative;
        padding-left: 20px;
    }
    .timeline-wrapper::before {
        content: '';
        position: absolute;
        top: 20px; bottom: 20px; left: 60px;
        width: 1px;
        background: #333;
    }
    .timeline-row {
        display: flex;
        align-items: center;
        position: relative;
        z-index: 2;
    }
    .time-col {
        width: 40px;
        text-align: right;
        font-weight: 700;
        font-size: var(--font-size-4xl);
        color: #00e5ff;
        margin-right: 15px;
    }
    .incident-card {
        flex-grow: 1;
        background-color: #1e1e1e;
        border-radius: 6px;
        padding: 12px 16px;
        display: flex;
        align-items: center;
    }
    .border-home { border-left: 3px solid #00e5ff; }
    .border-away { border-left: 3px solid #ff3d00; }
    .border-neutral { border-left: 3px solid #666; }

    .incident-badge {
        background: rgba(0, 229, 255, 0.1);
        padding: 2px 6px;
        border-radius: 4px;
    }
    .incident-text {
        font-size: var(--font-size-4xl);
        font-weight: 800;
        color: #ddd;
        letter-spacing: 0.5px;
    }
    .incident-score {
        background: #111;
        padding: 4px 10px;
        border-radius: 4px;
        font-weight: 900;
        font-size: var(--font-size-6xl);
        color: #fff;
    }

    /* LINEUPS */
    .border-right-dark { border-right: 1px solid #333; }
    .lineup-row { padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
    .shirt-number {
        background-color: #2a2a2a;
        color: #fff;
        font-size: var(--font-size-2xl);
        font-weight: bold;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .player-name {
        font-size: var(--font-size-4xl);
        color: #ccc;
        font-weight: 500;
        flex-grow: 1;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 40px 0;
    }
    @keyframes pulse {
        0% { transform: scale(0.95); opacity: 0.8; }
        50% { transform: scale(1.1); opacity: 1; }
        100% { transform: scale(0.95); opacity: 0.8; }
    }
</style>